import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Save, Send, Check, RefreshCw, Maximize2, Zap } from 'lucide-react';
import { adminGetEmailTemplate, adminSetEmailTemplate, adminPreviewEmailTemplate, adminSendTestEmail, adminSendRealEmailNow, type EmailTemplateOverrides } from '../../services/adminService';

/**
 * Admin email-template editor — edit the weekly-summary email's branding + default copy (Firestore-stored,
 * merged into every send), with a LIVE preview (renders the sample with the unsaved edits) and a test-send.
 * The mail endpoint is a clean no-op until configured, so a test-send reports "skipped" rather than failing.
 */
const FIELDS: Array<{ key: keyof EmailTemplateOverrides; label: string; placeholder: string; textarea?: boolean }> = [
  { key: 'brandName', label: 'Brand name (wordmark)', placeholder: 'Wisetree AI' },
  { key: 'subjectPrefix', label: 'Subject prefix', placeholder: '(none)' },
  { key: 'ctaLabel', label: 'Button label', placeholder: 'Open Wisetree AI' },
  { key: 'footerNote', label: 'Footer note', placeholder: "You're receiving this because you use Wisetree AI." },
  { key: 'introDefault', label: 'Default intro line', placeholder: "Here's what your specialists were up to this week.", textarea: true },
  { key: 'teaserDefault', label: 'Default "Next week" teaser', placeholder: 'A hook for what is coming next…', textarea: true },
];

const EmailTemplatePanel: React.FC<{ fullScreen?: boolean }> = ({ fullScreen = false }) => {
  const [ov, setOv] = useState<EmailTemplateOverrides>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [html, setHtml] = useState<string>('');
  const [previewing, setPreviewing] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testMsg, setTestMsg] = useState('');
  const [sending, setSending] = useState(false);
  // "Send the next email" — a REAL, dynamically-computed email for one user (their live activity + book + profile).
  const [realTo, setRealTo] = useState('');
  const [realMsg, setRealMsg] = useState('');
  const [sendingReal, setSendingReal] = useState(false);

  useEffect(() => {
    adminGetEmailTemplate().then((t) => setOv(t || {})).catch(() => { /* defaults */ }).finally(() => setLoading(false));
  }, []);

  // Debounced live preview whenever the overrides change.
  const key = useMemo(() => JSON.stringify(ov), [ov]);
  useEffect(() => {
    if (loading) return;
    let alive = true; setPreviewing(true);
    const id = setTimeout(() => {
      adminPreviewEmailTemplate(ov).then((r) => { if (alive) setHtml(r.html); }).catch(() => { /* keep last */ }).finally(() => { if (alive) setPreviewing(false); });
    }, 400);
    return () => { alive = false; clearTimeout(id); };
  }, [key, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k: keyof EmailTemplateOverrides, v: string) => setOv((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true); setSaved(false);
    try { const t = await adminSetEmailTemplate(ov); setOv(t); setSaved(true); setTimeout(() => setSaved(false), 2000); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setSending(true); setTestMsg('');
    try {
      const r = await adminSendTestEmail(testTo || undefined);
      const missing = r.skipped ? [!r.hasToken && 'token', !r.hasAccount && 'account'].filter(Boolean).join(' + ') : '';
      setTestMsg(
        r.skipped ? `Skipped — email not configured${missing ? ` (missing ${missing})` : ''}. Would send to ${r.to}`
          : r.sent ? `Sent to ${r.to}` : `Failed: ${r.error || 'unknown'}`,
      );
    } catch (e: any) { setTestMsg(e?.message || 'Send failed'); }
    finally { setSending(false); }
  };

  const sendReal = async () => {
    const email = realTo.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setRealMsg('Enter a valid user email'); return; }
    setSendingReal(true); setRealMsg('');
    try {
      const r = await adminSendRealEmailNow({ email });
      const kind = r.type === 'comeback' ? 'come-back' : 'weekly';
      setRealMsg(
        r.reason === 'cooldown' ? `On cooldown — sent to this user recently. Try again in ${r.retryInSec ?? 0}s.`
          : r.skipped ? `Skipped — ${r.reason || 'not configured'} (${kind} email)`
          : r.sent ? `Sent the real ${kind} email to ${email}` : `Failed: ${r.error || 'unknown'}`,
      );
    } catch (e: any) { setRealMsg(e?.message || 'Send failed'); }
    finally { setSendingReal(false); }
  };

  if (loading) return <div className="rounded-2xl border border-secondary-200 dark:border-secondary-800 p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-secondary-400" /></div>;

  return (
    <div className={fullScreen ? '' : 'rounded-2xl border border-secondary-200 dark:border-secondary-800 p-5'}>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-semibold">Email template <span className="text-secondary-400 font-normal text-sm">· weekly summary</span></h2>
        <div className="flex items-center gap-2">
          {!fullScreen && (
            <Link to="/admin/email-editor" className="inline-flex items-center gap-1.5 rounded-lg border border-secondary-300 dark:border-secondary-700 px-3 py-1.5 text-xs font-medium text-secondary-600 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-800">
              <Maximize2 className="w-3.5 h-3.5" /> Full screen
            </Link>
          )}
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-lg bg-secondary-900 dark:bg-white px-3 py-1.5 text-xs font-semibold text-white dark:text-secondary-900 hover:bg-black dark:hover:bg-secondary-100 disabled:opacity-40">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : saved ? <Check className="w-3.5 h-3.5" /> : <Save className="w-3.5 h-3.5" />} {saved ? 'Saved' : 'Save'}
          </button>
        </div>
      </div>

      <div className={`grid gap-4 ${fullScreen ? 'lg:grid-cols-[400px_1fr]' : 'lg:grid-cols-2'}`}>
        {/* Editor */}
        <div className="space-y-3">
          {FIELDS.map((f) => (
            <label key={f.key} className="block">
              <span className="text-xs font-medium text-secondary-600 dark:text-secondary-300">{f.label}</span>
              {f.textarea ? (
                <textarea value={(ov[f.key] as string) || ''} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder} rows={2}
                  className="mt-1 w-full rounded-lg border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-3 py-2 text-sm text-secondary-900 dark:text-white focus:outline-none focus:border-secondary-500" />
              ) : (
                <input value={(ov[f.key] as string) || ''} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder}
                  className="mt-1 w-full rounded-lg border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-3 py-2 text-sm text-secondary-900 dark:text-white focus:outline-none focus:border-secondary-500" />
              )}
            </label>
          ))}
          <div className="border-t border-secondary-100 dark:border-secondary-800 pt-3">
            <span className="text-xs font-medium text-secondary-600 dark:text-secondary-300">Send a test</span>
            <div className="mt-1 flex gap-2">
              <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@wisetree.ai (default admin)"
                className="flex-1 rounded-lg border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-3 py-2 text-sm text-secondary-900 dark:text-white focus:outline-none focus:border-secondary-500" />
              <button onClick={sendTest} disabled={sending}
                className="inline-flex items-center gap-1.5 rounded-lg border border-secondary-300 dark:border-secondary-700 px-3 py-2 text-xs font-semibold text-secondary-700 dark:text-secondary-200 hover:bg-secondary-50 dark:hover:bg-secondary-800 disabled:opacity-40">
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Test
              </button>
            </div>
            {testMsg && <p className="mt-1.5 text-[11px] text-secondary-500">{testMsg}</p>}
          </div>
          <div className="border-t border-secondary-100 dark:border-secondary-800 pt-3">
            <span className="text-xs font-medium text-secondary-600 dark:text-secondary-300">Send the next email <span className="font-normal text-secondary-400">· real data, for a user</span></span>
            <p className="mt-0.5 text-[11px] text-secondary-400">Fast-forwards this user's live weekly email (their activity, book + profile) with the real copywriter. Their unsubscribe still applies.</p>
            <div className="mt-1.5 flex gap-2">
              <input value={realTo} onChange={(e) => setRealTo(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendReal(); } }}
                placeholder="user@example.com" inputMode="email"
                className="flex-1 rounded-lg border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-3 py-2 text-sm text-secondary-900 dark:text-white focus:outline-none focus:border-secondary-500" />
              <button onClick={sendReal} disabled={sendingReal}
                className="inline-flex items-center gap-1.5 rounded-lg bg-secondary-900 dark:bg-white px-3 py-2 text-xs font-semibold text-white dark:text-secondary-900 hover:bg-black dark:hover:bg-secondary-100 disabled:opacity-40">
                {sendingReal ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />} Send the next email
              </button>
            </div>
            {realMsg && <p className="mt-1.5 text-[11px] text-secondary-500">{realMsg}</p>}
          </div>
        </div>

        {/* Live preview */}
        <div>
          <div className="mb-1 flex items-center gap-1.5 text-xs text-secondary-400">
            {previewing ? <RefreshCw className="w-3 h-3 animate-spin" /> : null} Live preview (sample data)
          </div>
          <iframe title="Email preview" srcDoc={html} className={`w-full rounded-lg border border-secondary-200 dark:border-secondary-800 bg-white ${fullScreen ? 'h-[calc(100vh-230px)] min-h-[560px]' : 'h-[560px]'}`} />
        </div>
      </div>
    </div>
  );
};

export default EmailTemplatePanel;
